import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { UserPlus, Trash2, Shield, Crown } from "lucide-react";

export default function AdminAdmins() {
  const [data, setData] = useState({ owner_email: "", admins: [] });
  const [form, setForm] = useState({ email: "", name: "" });
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admins").then(r => setData(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Email is required"); return; }
    setAdding(true);
    try {
      await api.post("/admins", form);
      toast.success(`${form.email} can now sign in as admin`);
      setForm({ email: "", name: "" });
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed to add admin");
    } finally { setAdding(false); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from admins?`)) return;
    try {
      await api.delete(`/admins/${encodeURIComponent(email)}`);
      toast.success("Admin removed");
      load();
    } catch (er) {
      toast.error(er.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <div className="p-10 max-w-3xl" data-testid="admin-admins">
      <p className="label-eyebrow mb-3">Team</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1a1a1a] mb-3">Administrators</h1>
      <p className="text-sm text-neutral-600 mb-10">Anyone listed here can sign in with Google using their email and manage the site.</p>

      <div className="bg-white border border-neutral-200 mb-8">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center gap-3 bg-[#FAF3F4]">
          <Crown size={16} className="text-[#6B1F2A]" />
          <div className="flex-1">
            <p className="text-sm font-medium text-[#1a1a1a]">{data.owner_email}</p>
            <p className="text-xs text-neutral-500">Owner · cannot be removed</p>
          </div>
          <span className="text-xs uppercase tracking-wider text-[#6B1F2A] font-medium">Owner</span>
        </div>
        {data.admins?.length === 0 ? (
          <p className="px-6 py-6 text-sm text-neutral-500 italic" data-testid="no-extra-admins">No additional administrators yet.</p>
        ) : (
          <ul>
            {data.admins.map(a => (
              <li key={a.id} className="px-6 py-4 flex items-center gap-3 border-b border-neutral-100 last:border-0" data-testid={`admin-row-${a.email}`}>
                <Shield size={16} className="text-neutral-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1a1a1a]">{a.name || a.email}</p>
                  {a.name && <p className="text-xs text-neutral-500">{a.email}</p>}
                  <p className="text-xs text-neutral-400 mt-0.5">Added {new Date(a.created_at).toLocaleDateString()} by {a.added_by}</p>
                </div>
                <button onClick={() => remove(a.email)} className="p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded" title="Remove admin" data-testid={`remove-admin-${a.email}`}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={add} className="bg-white border border-neutral-200 p-8" data-testid="add-admin-form">
        <h2 className="font-serif-display text-2xl text-[#1a1a1a] mb-5">Add an administrator</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Email <span className="text-[#6B1F2A]">*</span></Label>
            <Input type="email" required value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              placeholder="newadmin@example.com" data-testid="new-admin-email" />
          </div>
          <div>
            <Label>Name (optional)</Label>
            <Input value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Jane Doe" data-testid="new-admin-name" />
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-3 mb-5">
          The new admin must sign in via Google using this exact email address. They'll see the full admin console on first sign-in.
        </p>
        <button type="submit" disabled={adding} className="btn-primary-burgundy disabled:opacity-60" data-testid="add-admin-submit">
          <UserPlus size={14} /> {adding ? "Adding..." : "Add admin"}
        </button>
      </form>
    </div>
  );
}
